import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Clock, Tag, ArrowLeft, ChevronRight, Share2, Facebook, Twitter, Linkedin } from 'lucide-react';
import SEO, { breadcrumbSchema } from '../components/SEO';
import DOMPurify from 'dompurify';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  'Technical': '#E8520A',
  'Expeditions': '#2563eb',
  'News': '#16a34a',
  'Build Guides': '#9333ea',
  'Customer Stories': '#dc2626',
};

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/articles/${slug}`);
        if (!res.ok) throw new Error('Not found');
        const data = await res.json();
        setArticle(data.article);
        setRelated(data.related || []);
      } catch {
        setArticle(null);
      }
      setLoading(false);
    };
    fetchArticle();
    window.scrollTo(0, 0);
  }, [slug]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-[76px] bg-white flex items-center justify-center">
        <div className="animate-pulse space-y-4 w-full max-w-3xl px-6">
          <div className="h-6 bg-gray-100 rounded w-1/3" />
          <div className="h-10 bg-gray-100 rounded w-2/3" />
          <div className="h-64 bg-gray-100 rounded" />
          <div className="space-y-2">
            <div className="h-3 bg-gray-100 rounded" />
            <div className="h-3 bg-gray-100 rounded w-5/6" />
            <div className="h-3 bg-gray-100 rounded w-4/6" />
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen pt-[76px] bg-white flex flex-col items-center justify-center px-6">
        <h1 className="text-3xl font-black text-gray-900 mb-4">Article Not Found</h1>
        <p className="text-gray-500 mb-6">The article you're looking for doesn't exist or has been removed.</p>
        <Link to="/articles" className="text-sm font-bold uppercase tracking-wider hover:underline" style={{ color: '#E8520A' }}>
          Back to Articles
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-[76px]" style={{ backgroundColor: '#000' }}>
      <SEO
        title={article.title}
        description={article.excerpt}
        path={`/articles/${article.slug}`}
        image={article.featured_image}
        type="article"
        keywords={[...(article.categories || []), ...(article.tags || [])].join(', ')}
        jsonLd={[
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Articles', path: '/articles' },
            { name: article.title, path: `/articles/${article.slug}` }
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.featured_image || '',
            author: { '@type': 'Person', name: article.author },
            publisher: { '@type': 'Organization', name: 'Total Composites' },
            datePublished: article.publish_date,
            dateModified: article.updated_at || article.publish_date,
          }
        ]}
      />

      {/* Hero header — black, matches Media */}
      <section className="bg-black py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/articles"
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest mb-6 transition-colors hover:opacity-75"
            style={{ color: '#E8520A' }}
            data-testid="back-to-articles"
          >
            <ArrowLeft size={12} />
            All Articles
          </Link>

          {/* Categories */}
          <div className="flex flex-wrap gap-2 mb-4">
            {article.categories?.map(cat => (
              <Link
                key={cat}
                to={`/articles?category=${encodeURIComponent(cat)}`}
                className="px-2.5 py-1 text-xs font-black uppercase rounded"
                style={{ backgroundColor: CATEGORY_COLORS[cat] || '#E8520A', color: '#fff', fontSize: '0.58rem', letterSpacing: '0.08em' }}
              >
                {cat}
              </Link>
            ))}
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight leading-tight mb-4 uppercase" style={{ fontFamily: 'Inter, sans-serif' }} data-testid="article-title">
            {article.title}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-xs text-gray-400">
            <span className="font-medium text-gray-300">{article.author}</span>
            <span>{formatDate(article.publish_date)}</span>
            <span className="flex items-center gap-1">
              <Clock size={10} />
              {article.read_time} min read
            </span>
          </div>
        </div>
      </section>

      {/* Article Body — white section */}
      <section className="bg-white py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Featured Image */}
          {article.featured_image && (
            <div className="mb-10">
              <img
                src={article.featured_image}
                alt={article.title}
                className="w-full rounded-xl object-cover max-h-[500px]"
                data-testid="article-featured-image"
              />
            </div>
          )}

          {/* Body */}
          <article data-testid="article-body">
            <div
              className="article-content"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.body, { ADD_TAGS: ['iframe'], ADD_ATTR: ['target', 'style', 'class', 'allow', 'allowfullscreen', 'frameborder'] }) }}
            />
          </article>

          {/* Tags + Share */}
          <div className="border-t border-gray-200 pt-6 mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            {/* Tags */}
            {article.tags?.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <Tag size={12} className="text-gray-400" />
                {article.tags.map(tag => (
                  <span key={tag} className="px-2 py-0.5 text-xs text-gray-500 border border-gray-200 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Share buttons */}
            <div className="flex items-center gap-3" data-testid="share-buttons">
              <span className="text-xs text-gray-400 uppercase tracking-wider font-bold">Share</span>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank" rel="noopener noreferrer"
                className="text-gray-400 hover:text-gray-900 transition-colors"
                aria-label="Share on Facebook"
              >
                <Facebook size={16} />
              </a>
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(article.title)}`}
                target="_blank" rel="noopener noreferrer"
                className="text-gray-400 hover:text-gray-900 transition-colors"
                aria-label="Share on Twitter"
              >
                <Twitter size={16} />
              </a>
              <a
                href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(article.title)}`}
                target="_blank" rel="noopener noreferrer"
                className="text-gray-400 hover:text-gray-900 transition-colors"
                aria-label="Share on LinkedIn"
              >
                <Linkedin size={16} />
              </a>
              <button
                onClick={handleCopyLink}
                className="text-gray-400 hover:text-gray-900 transition-colors"
                aria-label="Copy link"
                data-testid="copy-link-btn"
              >
                <Share2 size={16} />
              </button>
              {copied && <span className="text-xs font-bold" style={{ color: '#16a34a' }}>Copied!</span>}
            </div>
          </div>
        </div>
      </section>

      {/* Related Articles — gray-50 section */}
      {related.length > 0 && (
        <section className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4 mb-10">
              <h2 className="text-2xl font-black text-gray-900 whitespace-nowrap" style={{ fontFamily: 'Inter, sans-serif' }}>
                Related Articles
              </h2>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map(r => (
                <Link
                  key={r.id}
                  to={`/articles/${r.slug}`}
                  className="group flex flex-col border border-gray-100 rounded-xl overflow-hidden shadow-sm hover:shadow-lg hover:border-orange-200 transition-all duration-300 bg-white"
                  style={{ textDecoration: 'none' }}
                  data-testid={`related-${r.slug}`}
                >
                  <div className="relative overflow-hidden" style={{ height: 160 }}>
                    {r.featured_image ? (
                      <img src={r.featured_image} alt={r.title} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-50">
                        <span className="text-3xl font-black text-gray-200">{r.title?.[0]}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4 border-t border-gray-100">
                    <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">{formatDate(r.publish_date)}</p>
                    <h3 className="text-xs font-black text-gray-900 uppercase leading-snug group-hover:text-orange-600 transition-colors line-clamp-2" style={{ letterSpacing: '0.04em' }}>{r.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
